import { mergeSxProps } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type KeyboardEvent, useCallback, useRef } from "react";

interface SegmentedControlOption<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly count?: number;
}

interface SegmentedControlProps<T extends string> {
  readonly ariaLabel?: string;
  readonly options: ReadonlyArray<SegmentedControlOption<T>>;
  readonly sx?: SxProps<Theme>;
  readonly value: T;
  readonly onChange: (value: T) => void;
}

export function SegmentedControl<T extends string>(props: SegmentedControlProps<T>) {
  const { ariaLabel, options, sx, value, onChange } = props;

  const activeIndex = options.findIndex((option) => option.value === value);
  const tablistRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      event.preventDefault();
      const currentIndex = options.findIndex((option) => option.value === value);
      const nextIndex =
        event.key === "ArrowRight"
          ? (currentIndex + 1) % options.length
          : (currentIndex - 1 + options.length) % options.length;
      onChange(options[nextIndex].value);

      const tabButtons = tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      tabButtons?.[nextIndex]?.focus();
    },
    [options, value, onChange]
  );

  return (
    <Box
      ref={tablistRef}
      aria-label={ariaLabel}
      role="tablist"
      sx={mergeSxProps(
        (theme) => ({
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          padding: theme.spacing(1),
          borderRadius: "100px",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.selected,
          width: "100%",
        }),
        sx
      )}
      onKeyDown={handleKeyDown}
    >
      {/* Sliding pill — inset track matches flex row so last segment keeps horizontal padding */}
      <Box
        aria-hidden="true"
        sx={(theme) => ({
          position: "absolute",
          inset: theme.spacing(1),
          pointerEvents: "none",
        })}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            sx={(theme) => ({
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              // One segment: inner track width minus (n−1)× flex gap, divided by n
              width: `calc((100% - ${theme.spacing(Math.max(0, options.length - 1))}) / ${options.length})`,
              borderRadius: "100px",
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[1],
              transform: `translateX(calc(${activeIndex} * (100% + ${theme.spacing(1)})))`,
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.short,
                easing: theme.transitions.easing.easeInOut,
              }),
            })}
          />
        </Box>
      </Box>

      {options.map((option) => (
        <SegmentedControlTab
          key={option.value}
          label={option.label}
          count={option.count}
          isSelected={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        />
      ))}
    </Box>
  );
}

interface SegmentedControlTabProps {
  readonly label: string;
  readonly count?: number;
  readonly isSelected: boolean;
  readonly onClick: () => void;
}

function SegmentedControlTab(props: SegmentedControlTabProps) {
  const { label, count, isSelected, onClick } = props;

  return (
    <Box
      component="button"
      type="button"
      role="tab"
      aria-selected={isSelected}
      tabIndex={isSelected ? 0 : -1}
      sx={(theme) => ({
        position: "relative",
        zIndex: 1,
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: theme.spacing(10),
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(4),
        borderRadius: "100px",
        border: "none",
        cursor: "pointer",
        backgroundColor: "transparent",
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
        },
      })}
      onClick={onClick}
    >
      <Box
        component="span"
        sx={(theme) => ({
          ...theme.typography.body2,
          fontWeight: theme.typography.fontWeightMedium,
          color: isSelected ? theme.palette.text.primary : theme.palette.text.secondary,
          whiteSpace: "nowrap",
          transition: theme.transitions.create("color", {
            duration: theme.transitions.duration.short,
            easing: theme.transitions.easing.easeInOut,
          }),
        })}
      >
        {label}
        {isDefined(count) && (
          <>
            {" "}
            <Box
              component="span"
              sx={(theme) => ({
                fontWeight: theme.typography.fontWeightRegular,
                color: isSelected ? theme.palette.text.secondary : theme.palette.text.disabled,
              })}
            >
              {count}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
